import { ReactNode, type JSX } from "react";
import { AriaSelectProps, useLabel } from "react-aria";
import { Select as AriaSelect } from "react-aria-components";

import Box from "ds/components/Box";
import FormFieldAriaLabel from "ds/components/Form/Field/AriaLabel";
import FormFieldHelperText from "ds/components/Form/Field/HelperText";
import { getColors } from "ds/components/Form/Field/utils";
import { getAriaDescribedBy } from "ds/components/Form/helpers";
import ListBox from "ds/components/ListBox";
import ListBoxPopover from "ds/components/ListBox/Popover";
import ListBoxScrollableWrapper from "ds/components/ListBox/ScrollableWrapper";
import TooltipInfo from "ds/components/TooltipInfo";
import { AnalyticsCommonProps } from "hooks/useAnalytics";
import useEnsureId from "hooks/useEnsureId";

import SelectEmptyCollection from "./EmptyCollection";
import SelectInput, { SelectInputProps } from "./Input";
import Item from "./Item";
import styles from "./styles.module.css";
import { BaseSelectItem } from "./types";

type SelectProps<Collection extends BaseSelectItem, Key extends string> = Omit<
  AriaSelectProps<Collection>,
  "children"
> & {
  id?: string;
  label?: string;
  helperText?: string;
  error?: string;
  placeholder?: string;
  value: string;
  items: Collection[];
  onChange: (value: Key) => void;
  renderInput?: (input: SelectInputProps) => ReactNode;
  children?: (Collection: Collection) => JSX.Element;
  tooltipInfo?: ReactNode;
  tooltipAnalyticsPage?: AnalyticsCommonProps["analyticsPage"];
  tooltipAnalyticsTitle?: AnalyticsCommonProps["analyticsTitle"];
  tooltipAnalyticsProps?: AnalyticsCommonProps["analyticsProps"];
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: SelectInputProps["size"];
  color?: SelectInputProps["color"];
};

const Select = <Collection extends BaseSelectItem, Key extends string>({
  id: propsId,
  children = (item) => <Item {...item} id={item.value} />,
  label,
  helperText,
  error,
  size,
  color,
  value,
  renderInput = (props) => <SelectInput {...props} />,
  onChange,
  items,
  tooltipInfo,
  tooltipAnalyticsPage,
  tooltipAnalyticsTitle,
  tooltipAnalyticsProps,
  placeholder = "Select an option",
  isDisabled: propsIsDisabled,
  isLoading,
  ...rest
}: SelectProps<Collection, Key>) => {
  const id = useEnsureId(propsId);
  const { primaryIconColor } = getColors(color);
  const isDisabled = propsIsDisabled || !items?.length;

  const { labelProps, fieldProps } = useLabel({
    label,
    id,
  });

  return (
    <Box gap="small" direction="column" fullWidth>
      {label && (
        <Box gap="small" align="center" fullWidth>
          <FormFieldAriaLabel {...labelProps} label={label} color={color} />
          {tooltipInfo && (
            <TooltipInfo
              variant="modal"
              iconColor={primaryIconColor}
              analyticsPage={tooltipAnalyticsPage}
              analyticsTitle={tooltipAnalyticsTitle}
              analyticsProps={tooltipAnalyticsProps}
            >
              {tooltipInfo}
            </TooltipInfo>
          )}
        </Box>
      )}
      <AriaSelect
        className={styles.select}
        selectedKey={value}
        onSelectionChange={(key) => onChange(key as Key)}
        isInvalid={!!error}
        isDisabled={isDisabled}
        placeholder={placeholder}
        {...fieldProps}
      >
        <Box gap="small" direction="column">
          {renderInput({
            id,
            "aria-describedby": getAriaDescribedBy(id, error, helperText),
            isError: !!error,
            isDisabled,
            isLoading,
            size,
            color,
            ...rest,
          })}
          <FormFieldHelperText helperText={helperText} error={error} id={id} color={color} />
        </Box>
        <ListBoxPopover>
          <ListBoxScrollableWrapper>
            <ListBox<Collection>
              items={items}
              renderEmptyState={() => <SelectEmptyCollection isLoading={isLoading} />}
            >
              {children}
            </ListBox>
          </ListBoxScrollableWrapper>
        </ListBoxPopover>
      </AriaSelect>
    </Box>
  );
};

Select.displayName = "DS.Select";

export default Select;
